<template>
  <div class="w-full max-w-5xl space-y-5 pb-8 text-foxy-text">
    <header class="space-y-1">
      <h1 class="text-2xl font-extrabold tracking-tight sm:text-3xl">Збережене</h1>
      <p class="text-sm text-foxy-text-secondary">Те, до чого хочеться повернутися</p>
    </header>

    <section class="space-y-3" aria-label="Фільтри збереженого">
      <div class="scrollbar-hide overflow-x-auto pb-1">
        <div class="flex min-w-max items-center gap-2">
          <button
            v-for="type in favoriteTypes"
            :key="type.id"
            type="button"
            class="inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foxy-action/60"
            :class="activeType === type.id ? 'border-foxy-action bg-foxy-elevated text-foxy-text' : 'border-foxy-border bg-foxy-surface/80 text-foxy-text-secondary hover:bg-foxy-elevated hover:text-foxy-text'"
            :aria-pressed="activeType === type.id ? 'true' : 'false'"
            @click="setActiveType(type.id)"
          >
            <span>{{ type.label }}</span>
            <span class="rounded-full bg-foxy-background/70 px-1.5 py-0.5 text-[11px] tabular-nums">{{ type.count }}</span>
          </button>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <div class="relative min-w-0 flex-1">
          <i class="fas fa-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-foxy-text-secondary" aria-hidden="true"></i>
          <input
            v-model.trim="searchQuery"
            type="search"
            class="foxy-input !py-2 !pl-9 !pr-3 text-sm"
            placeholder="Пошук у збереженому"
            aria-label="Пошук у збереженому"
            @input="schedulePublicationReload"
          >
        </div>
        <button
          type="button"
          class="library-sort-direction acc-btn shrink-0"
          :aria-label="sortDirectionLabel"
          :title="sortDirectionLabel"
          @click="toggleSortDirection"
        >
          <span class="library-sort-direction-icon" aria-hidden="true">{{ sortDirection === 'desc' ? '↓' : '↑' }}</span>
        </button>
      </div>
      <p v-if="searchQuery" class="text-xs text-foxy-text-secondary">Знайдено: {{ publicationMatchedTotal }}</p>
    </section>

    <div v-if="favoriteError" class="rounded-2xl border border-foxy-error/40 bg-foxy-error/10 p-4 text-sm text-foxy-error" role="alert">
      {{ favoriteError }}
    </div>

    <section class="space-y-3" aria-live="polite">
      <template v-if="favoriteItems.length">
        <article
          v-for="item in favoriteItems"
          :key="`${item.family}-${item.id}`"
          class="group cursor-pointer rounded-2xl border border-foxy-border/80 bg-foxy-surface/75 p-4 shadow-sm transition hover:border-foxy-action/45 hover:bg-foxy-surface focus-visible:border-foxy-action focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20 sm:p-5"
          role="link"
          tabindex="0"
          :aria-label="cardAriaLabel(item)"
          data-saved-card
          @click="openSavedItem(item)"
          @keydown.enter.prevent="openSavedItem(item)"
          @keydown.space.prevent="openSavedItem(item)"
        >
          <div v-if="item.family === 'hashtag'" class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div class="flex min-w-0 items-center gap-3">
              <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-foxy-elevated text-xl font-extrabold text-foxy-action" aria-hidden="true">#</div>
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <a :href="item.url" class="break-words text-lg font-extrabold text-foxy-text hover:text-foxy-action" @click.stop @keydown.stop>#{{ item.name }}</a>
                  <span class="text-xs text-foxy-text-secondary">{{ item.saved_at_human || 'щойно' }}</span>
                </div>
                <p v-if="item.blocked" class="mt-1 text-sm font-semibold text-foxy-text-secondary">
                  <i class="fas fa-lock mr-1" aria-hidden="true"></i>Заблоковано
                </p>
                <p v-else class="mt-1 text-sm text-foxy-text-secondary">{{ publicationCountLabel(item.visible_publications_count) }}</p>
              </div>
            </div>
            <button
              type="button"
              class="acc-btn shrink-0 bg-foxy-elevated text-foxy-text disabled:cursor-wait disabled:opacity-70"
              :disabled="Boolean(favoritePending[`hashtag-${item.id}`])"
              aria-pressed="true"
              aria-label="Прибрати зі збереженого"
              title="Прибрати зі збереженого"
              @click.stop="removeFavorite(item)"
              @keydown.stop
            >
              <i class="fas fa-bookmark" aria-hidden="true"></i>
              {{ favoritePending[`hashtag-${item.id}`] ? 'Зачекайте…' : 'Збережено' }}
            </button>
          </div>

          <div v-else-if="item.family === 'discussion'" class="min-w-0">
            <div class="flex flex-col items-start gap-3 sm:flex-row sm:justify-between" data-saved-discussion-header>
              <a v-if="item.publisher" :href="publisherUrl(item.publisher)" class="flex w-full min-w-0 items-center gap-2 rounded-lg text-foxy-text hover:text-foxy-action focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20 sm:w-auto" data-saved-discussion-identity @click.stop @keydown.stop>
                <user-avatar :src="item.publisher.avatar || ''" :name="item.publisher.name" size="sm" />
                <span class="min-w-0 text-xs leading-tight">
                  <span v-if="item.publisher.name" class="block truncate font-semibold">{{ item.publisher.name }}</span>
                  <span class="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-foxy-text-secondary">
                    <span class="truncate">@{{ item.publisher.username }}</span>
                    <span aria-hidden="true">·</span>
                    <span class="whitespace-nowrap" data-saved-publication-time>{{ item.saved_at_human || 'щойно' }}</span>
                  </span>
                </span>
              </a>
              <div class="flex w-full flex-wrap items-center justify-start gap-2 text-xs text-foxy-text-secondary sm:w-auto sm:shrink-0 sm:justify-end" data-saved-discussion-classification>
                <span
                  :class="[
                    'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-semibold',
                    item.is_closed
                      ? 'border-foxy-border bg-foxy-elevated text-foxy-text'
                      : 'border-foxy-success/55 bg-foxy-success/15 text-foxy-text',
                  ]"
                  :data-discussion-state="item.is_closed ? 'closed' : 'open'"
                  data-saved-discussion-status
                >
                  <span :class="['h-1.5 w-1.5 rounded-full', item.is_closed ? 'bg-foxy-text-secondary/60' : 'bg-foxy-success']" aria-hidden="true"></span>
                  {{ item.is_closed ? 'Закрита' : 'Відкрита' }}
                </span>
                <span class="acc-pill !px-2.5 !py-0.5" data-saved-publication-type>{{ item.publicationLabel }}</span>
              </div>
            </div>

            <a :href="item.url" class="mt-3 block break-words text-lg font-extrabold leading-snug text-foxy-text hover:text-foxy-action" @click.stop @keydown.stop>
              {{ item.title }}
            </a>

            <div v-if="item.works && item.works.length" class="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foxy-text-secondary">
              <i class="fas fa-book-open" aria-hidden="true"></i>
              <template v-for="(work, workIndex) in item.works">
                <span v-if="workIndex" :key="`discussion-work-separator-${work.id}`" aria-hidden="true">·</span>
                <a :key="`discussion-work-${work.id}`" :href="work.url" class="font-semibold hover:text-foxy-action hover:underline" @click.stop @keydown.stop>{{ work.title }}</a>
              </template>
              <span v-if="item.works_overflow_count">+{{ item.works_overflow_count }}</span>
            </div>

            <p class="saved-discussion-preview mt-3 break-words text-sm text-foxy-text-secondary" data-saved-discussion-preview>
              {{ item.content || 'Без тексту' }}
            </p>

            <div class="mt-3 flex items-center justify-end gap-1 border-t border-foxy-border/70 pt-3" @click.stop @keydown.stop>
              <a
                v-if="!item.is_closed"
                :href="item.url"
                class="foxy-btn-secondary !px-3 !py-2 !text-sm"
                :aria-label="replyActionLabel(item)"
                title="Відповісти"
                data-saved-reply-action
                @click.stop
                @keydown.stop
              >
                <i class="fas fa-reply" aria-hidden="true"></i>
                <span>{{ replyActionLabel(item) }}</span>
              </a>
              <button
                type="button"
                class="acc-btn shrink-0 bg-foxy-elevated text-foxy-text disabled:cursor-wait disabled:opacity-70"
                :disabled="Boolean(favoritePending[`discussion-${item.id}`])"
                aria-pressed="true"
                aria-label="Прибрати зі збереженого"
                title="Прибрати зі збереженого"
                data-saved-bookmark-action
                @click.stop="removeFavorite(item)"
                @keydown.stop
              >
                <i class="fas fa-bookmark" aria-hidden="true"></i>
                {{ favoritePending[`discussion-${item.id}`] ? 'Зачекайте…' : 'Збережено' }}
              </button>
              <span data-saved-report-action @click.stop @keydown.stop>
                <report-button
                  comfortable-trigger
                  report-label="Поскаржитись"
                  reportable-type="App\\Models\\Discussion"
                  :reportable-id="item.id"
                  :content-preview="item.content || ''"
                  :content-url="item.url"
                  :share-url="canonicalShareUrl(item)"
                  :share-title="shareTitle(item)"
                />
              </span>
            </div>
          </div>

          <div v-else class="flex items-start gap-3 sm:gap-4">
            <a :href="item.book_url" class="shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20" @click.stop @keydown.stop>
              <img
                :src="resolveBookCover(item.book_cover)"
                class="h-24 w-16 rounded-lg border border-foxy-border object-cover sm:h-28 sm:w-[4.75rem]"
                :alt="`Обкладинка книги «${item.book_title}»`"
                @error="onCoverError"
              >
            </a>
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-x-3 gap-y-1" data-saved-publication-meta>
                <span class="text-xs text-foxy-text-secondary" data-saved-publication-time>{{ item.saved_at_human || 'щойно' }}</span>
                <span class="acc-pill ml-auto !px-2.5 !py-0.5" data-saved-publication-type>{{ item.publicationLabel }}</span>
              </div>
              <a :href="item.book_url" class="mt-2 block text-base font-extrabold leading-snug text-foxy-text hover:text-foxy-action sm:text-lg" @click.stop @keydown.stop>
                {{ item.book_title }}
              </a>
              <p v-if="item.book_author_links && item.book_author_links.length" class="mt-0.5 text-xs text-foxy-text-secondary">
                <template v-for="(author, authorIndex) in item.book_author_links">
                  <span v-if="authorIndex" :key="`separator-${author.url}`">, </span>
                  <a :key="author.url" :href="author.url" class="hover:text-foxy-action hover:underline" @click.stop @keydown.stop>{{ author.name }}</a>
                </template>
              </p>
              <div
                v-if="item.family === 'review' && item.status === 'blocked'"
                class="mt-2 rounded-lg border-2 border-red-400 bg-red-50/70 p-3 dark:border-red-700 dark:bg-red-900/20"
                data-saved-review-blocked
              >
                <p
                  class="saved-review-preview pointer-events-none select-none break-words text-sm text-foxy-text-secondary blur-sm filter"
                  aria-hidden="true"
                  data-saved-review-protected-preview
                >
                  {{ item.content || 'Без тексту' }}
                </p>
                <div class="mt-3 flex items-start gap-2 border-t border-red-200 pt-2 dark:border-red-800">
                  <i class="fas fa-exclamation-triangle mt-0.5 shrink-0 text-red-700 dark:text-red-400" aria-hidden="true"></i>
                  <div class="min-w-0 flex-1">
                    <p class="text-xs font-semibold text-red-900 dark:text-red-300">Контент заблоковано адміністрацією сайту</p>
                    <p v-if="item.moderation_reason" class="mt-1 text-xs text-red-800 dark:text-red-400">Причина: {{ item.moderation_reason }}</p>
                  </div>
                </div>
              </div>
              <div
                v-else-if="item.family === 'review' && isSpoilerProtected(item)"
                class="relative mt-2 min-h-20 overflow-hidden rounded-lg"
                data-saved-review-spoiler
              >
                <p
                  class="saved-review-preview pointer-events-none select-none break-words text-sm text-foxy-text-secondary blur-sm filter"
                  aria-hidden="true"
                  data-saved-review-protected-preview
                >
                  {{ item.content || 'Без тексту' }}
                </p>
                <div class="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-foxy-surface/70 px-3 text-center backdrop-blur-sm" data-saved-review-spoiler-gate>
                  <div>
                    <p class="text-xs font-semibold text-foxy-text drop-shadow-sm">Публікація містить спойлер</p>
                    <button
                      type="button"
                      class="mt-2 rounded-lg bg-foxy-action px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-foxy-action/90 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                      :aria-expanded="revealedSpoilerIds[String(item.id)] ? 'true' : 'false'"
                      @click.stop="revealSpoiler(item)"
                      @keydown.stop
                    >
                      Показати спойлер
                    </button>
                  </div>
                </div>
              </div>
              <p
                v-else
                :class="[
                  'saved-publication-preview mt-2 break-words text-sm text-foxy-text-secondary',
                  item.family === 'review' ? 'saved-review-preview' : 'saved-quote-preview',
                ]"
                data-saved-publication-preview
                :data-saved-review-preview="item.family === 'review' ? '' : null"
                :data-saved-quote-preview="item.family === 'quote' ? '' : null"
              >
                <span v-if="item.family === 'quote'" aria-hidden="true">«</span>{{ item.content || 'Без тексту' }}<span v-if="item.family === 'quote'" aria-hidden="true">»</span>
              </p>
              <div class="mt-3 flex flex-col gap-3 border-t border-foxy-border/70 pt-3 sm:flex-row sm:items-center sm:justify-between">
                <a v-if="item.publisher" :href="publisherUrl(item.publisher)" class="flex min-w-0 items-center gap-2 rounded-lg text-foxy-text hover:text-foxy-action focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20" @click.stop @keydown.stop>
                  <user-avatar :src="item.publisher.avatar || ''" :name="item.publisher.name" size="sm" />
                  <span class="min-w-0 truncate text-xs leading-tight">
                    <span v-if="item.publisher.name" class="font-semibold">{{ item.publisher.name }}</span>
                    <span :class="['text-foxy-text-secondary', item.publisher.name ? 'ml-1' : '']">@{{ item.publisher.username }}</span>
                  </span>
                </a>
                <div class="flex shrink-0 items-center gap-1 self-start sm:self-auto" @click.stop @keydown.stop>
                  <a
                    v-if="item.family === 'review'"
                    :href="item.url"
                    class="foxy-btn-secondary !px-3 !py-2 !text-sm"
                    :aria-label="replyActionLabel(item)"
                    title="Відповісти"
                    data-saved-reply-action
                    @click.stop
                    @keydown.stop
                  >
                    <i class="fas fa-reply" aria-hidden="true"></i>
                    <span>{{ replyActionLabel(item) }}</span>
                  </a>
                  <button
                    type="button"
                    class="acc-btn shrink-0 bg-foxy-elevated text-foxy-text disabled:cursor-wait disabled:opacity-70"
                    :disabled="Boolean(favoritePending[`${item.family}-${item.id}`])"
                    aria-pressed="true"
                    aria-label="Прибрати зі збереженого"
                    title="Прибрати зі збереженого"
                    data-saved-bookmark-action
                    @click.stop="removeFavorite(item)"
                    @keydown.stop
                  >
                    <i class="fas fa-bookmark" aria-hidden="true"></i>
                    {{ favoritePending[`${item.family}-${item.id}`] ? 'Зачекайте…' : 'Збережено' }}
                  </button>
                  <span v-if="reportableType(item)" data-saved-report-action @click.stop @keydown.stop>
                    <report-button
                      comfortable-trigger
                      :reportable-type="reportableType(item)"
                      :reportable-id="item.id"
                      :content-preview="item.content || ''"
                      :content-url="item.url"
                      :share-url="canonicalShareUrl(item)"
                      :share-title="shareTitle(item)"
                    />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </article>
      </template>

      <div v-else-if="publicationLoaded && !publicationLoading" class="rounded-2xl border border-dashed border-foxy-border bg-foxy-surface/45 px-5 py-10 text-center">
        <div class="text-base font-extrabold text-foxy-text">{{ emptyTitle }}</div>
        <p class="mx-auto mt-1 max-w-md text-sm text-foxy-text-secondary">{{ emptyDescription }}</p>
      </div>

      <template v-if="publicationLoading && !favoriteItems.length">
        <div v-for="index in 3" :key="`saved-skeleton-${index}`" class="animate-pulse rounded-2xl border border-foxy-border/70 bg-foxy-surface/60 p-4 sm:p-5">
          <div class="flex gap-4">
            <div class="h-24 w-16 shrink-0 rounded-lg bg-foxy-elevated"></div>
            <div class="flex-1 space-y-3 py-1">
              <div class="h-3 w-24 rounded bg-foxy-elevated"></div>
              <div class="h-4 w-2/3 rounded bg-foxy-elevated"></div>
              <div class="h-3 w-full rounded bg-foxy-elevated"></div>
              <div class="h-3 w-4/5 rounded bg-foxy-elevated"></div>
            </div>
          </div>
        </div>
      </template>

      <div v-if="publicationHasMore || publicationLoadingMore" class="flex justify-center pt-1">
        <button type="button" class="acc-btn" :disabled="publicationLoading" @click="loadSavedPublications()">
          {{ publicationLoadingMore ? 'Завантаження…' : 'Показати ще' }}
        </button>
      </div>
    </section>
  </div>
</template>

<script lang="js">
import axios from 'axios';
import ReportButton from '../../components/ReportButton.vue';
import { formatCount } from '../../utils/ukrainianPluralization';
import {
  acknowledgeReviewSpoiler,
  currentViewerId,
  reviewSpoilerAcknowledgedEvent,
  shouldProtectReviewSpoiler,
} from '../../utils/reviewSpoiler';

export default {
  name: 'ProfileSaved',
  components: { ReportButton },
  props: {
    dashboard: { type: Object, default: () => ({}) },
    isOwner: { type: Boolean, default: false },
  },
  data() {
    return {
      activeType: 'all',
      sortDirection: 'desc',
      searchQuery: '',
      publicationItems: [],
      publicationNextCursor: null,
      publicationHasMore: false,
      publicationTotals: { all: 0, review: 0, quote: 0, discussion: 0, hashtag: 0 },
      publicationMatchedTotal: 0,
      publicationLoading: false,
      publicationLoadingMore: false,
      publicationLoaded: false,
      publicationRequestVersion: 0,
      publicationSearchTimer: null,
      favoriteError: '',
      favoritePending: {},
      revealedSpoilerIds: {},
      spoilerAcknowledgedHandler: null,
    };
  },
  computed: {
    favoriteTypes() {
      return [
        { id: 'all', label: 'Усі', count: Number(this.publicationTotals.all || 0) },
        { id: 'review', label: 'Рецензії та відгуки', count: Number(this.publicationTotals.review || 0) },
        { id: 'quote', label: 'Цитати', count: Number(this.publicationTotals.quote || 0) },
        { id: 'discussion', label: 'Дискусії', count: Number(this.publicationTotals.discussion || 0) },
        { id: 'hashtag', label: 'Хештеги', count: Number(this.publicationTotals.hashtag || 0) },
      ];
    },
    favoriteItems() {
      return this.publicationItems.map((item) => ({
        ...item,
        publicationLabel: item.family === 'review'
          ? this.reviewTypeLabel(item?.review_type)
          : (item.family === 'quote' ? 'Цитата' : (item.family === 'discussion' ? 'Дискусія' : '')),
      }));
    },
    sortDirectionLabel() {
      return this.sortDirection === 'desc' ? 'Спочатку нові' : 'Спочатку старі';
    },
    emptyTitle() {
      if (this.searchQuery) return 'Нічого не знайдено';
      if (this.activeType === 'review') return 'Збережених рецензій і відгуків поки немає';
      if (this.activeType === 'quote') return 'Збережених цитат поки немає';
      if (this.activeType === 'discussion') return 'Збережених дискусій поки немає';
      if (this.activeType === 'hashtag') return 'Збережених хештегів поки немає';
      return 'Тут поки нічого немає';
    },
    emptyDescription() {
      if (this.searchQuery) return 'Спробуйте змінити запит або вибрати інший тип збереженого.';
      return 'Зберігайте цікаві публікації та хештеги, щоб легко повернутися до них пізніше.';
    },
  },
  methods: {
    reviewTypeLabel(reviewType) {
      if (reviewType === 'review') return 'Рецензія';
      if (reviewType === 'opinion') return 'Відгук';
      return '';
    },
    publicationCountLabel(count) {
      return formatCount(count || 0, 'публікація', 'публікації', 'публікацій');
    },
    openSavedItem(item) {
      if (item?.url) window.location.assign(item.url);
    },
    cardAriaLabel(item) {
      if (item.family === 'hashtag') return `Відкрити хештег #${item.name}`;
      if (item.family === 'discussion') return `Відкрити збережену дискусію: ${item.title}`;
      return `Відкрити збережений матеріал: ${item.book_title}`;
    },
    reportableType(item) {
      if (item.family === 'review') return 'App\\Models\\Review';
      if (item.family === 'quote') return 'App\\Models\\Quote';
      if (item.family === 'discussion') return 'App\\Models\\Discussion';
      return '';
    },
    canonicalShareUrl(item) {
      const canonicalPath = typeof item?.url === 'string' ? item.url.trim() : '';
      if (!canonicalPath) return '';

      try {
        return new URL(canonicalPath, window.location.origin).href;
      } catch (error) {
        return canonicalPath;
      }
    },
    shareTitle(item) {
      if (item.family === 'discussion') return item.title ? `Дискусія: ${item.title}` : 'Дискусія';
      if (item.family === 'quote') return item.book_title ? `Цитата з книги «${item.book_title}»` : 'Цитата';

      const label = this.reviewTypeLabel(item.review_type) || 'Публікація';
      return item.book_title ? `${label} на книгу «${item.book_title}»` : label;
    },
    replyActionLabel(item) {
      const count = Number(item?.family === 'discussion' ? item?.replies_count : item?.comments_count) || 0;
      return count > 0 ? `Відповісти · ${count}` : 'Відповісти';
    },
    isSpoilerProtected(item) {
      return shouldProtectReviewSpoiler(
        item,
        currentViewerId(),
        Boolean(this.revealedSpoilerIds[String(item.id)]),
      );
    },
    revealSpoiler(item) {
      acknowledgeReviewSpoiler(item.id);
      this.$set(this.revealedSpoilerIds, String(item.id), true);
    },
    async setActiveType(type) {
      if (type === this.activeType) return;
      if (this.publicationSearchTimer) {
        clearTimeout(this.publicationSearchTimer);
        this.publicationSearchTimer = null;
      }
      this.activeType = type;
      await this.loadSavedPublications(true);
    },
    async toggleSortDirection() {
      this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
      await this.loadSavedPublications(true);
    },
    schedulePublicationReload() {
      if (this.publicationSearchTimer) clearTimeout(this.publicationSearchTimer);
      this.publicationSearchTimer = setTimeout(() => {
        this.loadSavedPublications(true);
      }, 300);
    },
    async loadSavedPublications(reset = false, preserveItemsDuringReset = false) {
      if (!this.isOwner) {
        this.publicationLoaded = true;
        return;
      }
      if (!reset && (this.publicationLoading || (this.publicationLoaded && !this.publicationHasMore))) return;

      if (reset) {
        this.publicationRequestVersion += 1;
        if (!preserveItemsDuringReset) this.publicationItems = [];
        this.publicationNextCursor = null;
        this.publicationHasMore = false;
        this.publicationLoaded = false;
      }
      const requestVersion = this.publicationRequestVersion;
      this.publicationLoading = true;
      this.publicationLoadingMore = !reset;
      this.favoriteError = '';
      const params = { type: this.activeType, sort: this.sortDirection };
      if (this.publicationNextCursor) params.cursor = this.publicationNextCursor;
      const query = String(this.searchQuery || '').trim();
      if (query) params.q = query;

      try {
        const { data } = await axios.get('/api/account/saved/publications', { params });
        if (requestVersion !== this.publicationRequestVersion) return;
        const existingItems = reset ? [] : this.publicationItems;
        const byKey = new Map(existingItems.map((item) => [`${item.family}-${item.id}`, item]));
        (data.items || []).forEach((item) => byKey.set(`${item.family}-${item.id}`, item));
        this.publicationItems = [...byKey.values()];
        this.publicationNextCursor = data.next_cursor || null;
        this.publicationHasMore = Boolean(data.has_more);
        this.publicationTotals = {
          all: Number(data.totals?.all || 0),
          review: Number(data.totals?.review || 0),
          quote: Number(data.totals?.quote || 0),
          discussion: Number(data.totals?.discussion || 0),
          hashtag: Number(data.totals?.hashtag || 0),
        };
        this.publicationMatchedTotal = Number(data.matched_total || 0);
        this.publicationLoaded = true;
      } catch (error) {
        if (requestVersion === this.publicationRequestVersion) {
          this.favoriteError = error?.response?.data?.message || 'Не вдалося завантажити збережені публікації.';
        }
      } finally {
        if (requestVersion === this.publicationRequestVersion) {
          this.publicationLoading = false;
          this.publicationLoadingMore = false;
        }
      }
    },
    async removeFavorite(item) {
      const family = item.family || item.type;
      const key = `${family}-${item.id}`;
      if (this.favoritePending[key]) return;
      if (family === 'hashtag' ? !item.slug : (family === 'discussion' ? !item.id : !item.book_slug)) return;

      this.$set(this.favoritePending, key, true);
      this.favoriteError = '';
      try {
        let response;
        if (family === 'hashtag') {
          response = await axios.delete(`/hashtags/${encodeURIComponent(item.slug)}/saved`);
          if (response.data.saved !== false) throw new Error('Unexpected Saved Hashtag state.');
        } else if (family === 'discussion') {
          response = await axios.delete(`/discussions/${item.id}/saved`);
          if (response.data.saved !== false) throw new Error('Unexpected Saved Discussion state.');
        } else {
          const endpoint = family === 'review'
            ? `/books/${encodeURIComponent(item.book_slug)}/reviews/${item.id}/favorite`
            : `/books/${encodeURIComponent(item.book_slug)}/quotes/${item.id}/favorite`;
          response = await axios.post(endpoint);
          if (response.data.is_favorited !== false) throw new Error('Unexpected Saved state.');
        }
        this.publicationItems = this.publicationItems.filter(
          (candidate) => `${candidate.family}-${candidate.id}` !== key,
        );
        await this.loadSavedPublications(true, true);
      } catch (error) {
        this.favoriteError = error?.response?.data?.message || 'Не вдалося прибрати зі збереженого.';
      } finally {
        this.$delete(this.favoritePending, key);
      }
    },
    resolveBookCover(cover) {
      return cover || '/images/placeholders/book-cover.svg';
    },
    onCoverError(event) {
      const fallback = '/images/placeholders/book-cover.svg';
      if (event?.target && !String(event.target.src || '').includes(fallback)) {
        event.target.src = fallback;
      }
    },
    publisherUrl(publisher) {
      return publisher?.username
        ? `/account/u/${encodeURIComponent(publisher.username)}/overview`
        : '#';
    },
  },
  mounted() {
    this.spoilerAcknowledgedHandler = (event) => {
      const reviewId = event.detail?.reviewId;
      if (reviewId !== null && reviewId !== undefined) {
        this.$set(this.revealedSpoilerIds, String(reviewId), true);
      }
    };
    window.addEventListener(reviewSpoilerAcknowledgedEvent(), this.spoilerAcknowledgedHandler);
    this.loadSavedPublications(true);
  },
  beforeDestroy() {
    if (this.publicationSearchTimer) clearTimeout(this.publicationSearchTimer);
    if (this.spoilerAcknowledgedHandler) {
      window.removeEventListener(reviewSpoilerAcknowledgedEvent(), this.spoilerAcknowledgedHandler);
    }
  },
};
</script>

<style scoped>
.saved-review-preview {
  display: -webkit-box;
  max-height: 9.75em;
  overflow: hidden;
  line-height: 1.625;
  white-space: normal;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 6;
  line-clamp: 6;
}

.saved-quote-preview {
  display: -webkit-box;
  max-height: 8.125em;
  overflow: hidden;
  line-height: 1.625;
  white-space: normal;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  line-clamp: 5;
}

.saved-discussion-preview {
  display: -webkit-box;
  max-height: 9.75em;
  overflow: hidden;
  line-height: 1.625;
  white-space: normal;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 6;
  line-clamp: 6;
}
</style>
